import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { TICKET_PRIORITIES, TICKET_PRIORITY_META } from '@/features/tickets/model';
import {
  useSaveFeedbackTask,
  type FeedbackTaskWithContext,
} from '@/features/customerFeedback/hooks';
import {
  FEEDBACK_TASK_KINDS,
  FEEDBACK_TASK_KIND_META,
  describeFeedbackError,
} from '@/features/customerFeedback/model';
import type { CustomerFeedbackTaskKind, TicketPriority } from '@/types/database';

interface FeedbackTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedbackId: string | null;
  /** Edit this task; omit to add a new one by hand. */
  existing?: FeedbackTaskWithContext | null;
}

/** Add a task the model missed, or tidy a suggestion before sending it. */
export function FeedbackTaskDialog({
  open,
  onOpenChange,
  feedbackId,
  existing = null,
}: FeedbackTaskDialogProps) {
  const save = useSaveFeedbackTask();
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [kind, setKind] = useState<CustomerFeedbackTaskKind>('other');
  const [priority, setPriority] = useState<TicketPriority>('medium');

  useEffect(() => {
    if (!open) return;
    setTitle(existing?.title ?? '');
    setDetails(existing?.details ?? '');
    setKind(existing?.kind ?? 'other');
    setPriority(existing?.suggested_priority ?? 'medium');
  }, [open, existing]);

  const canSubmit = !!feedbackId && title.trim().length > 0 && !save.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !feedbackId) return;
    try {
      await save.mutateAsync({
        id: existing?.id,
        feedbackId,
        title,
        details: details || null,
        kind,
        priority,
        categoryId: existing?.suggested_category_id ?? null,
      });
      toast.success(existing ? 'Task updated.' : 'Task added.');
      onOpenChange(false);
    } catch (err) {
      toast.error(describeFeedbackError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit} className="min-w-0 space-y-5">
          <DialogHeader>
            <DialogTitle>{existing ? 'Edit task' : 'Add a task'}</DialogTitle>
            <DialogDescription>
              {existing
                ? 'Adjust the wording before you send it. Sending opens the full request form.'
                : 'Something the extraction missed? Add it here, then send it like any other task.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="task-title">Task</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Quote a black finish for Acme's next order"
              maxLength={160}
              autoFocus
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-details">Details</Label>
            <Textarea
              id="task-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="What, for whom, why — quantities, dates, order refs…"
              className="min-h-[96px]"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as CustomerFeedbackTaskKind)}>
                <SelectTrigger className="h-10 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FEEDBACK_TASK_KINDS.map((k) => {
                    const meta = FEEDBACK_TASK_KIND_META[k];
                    return (
                      <SelectItem key={k} value={k}>
                        <span className="inline-flex items-center gap-2">
                          <meta.icon
                            className="h-3.5 w-3.5 text-muted-foreground"
                            strokeWidth={1.8}
                          />
                          {meta.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{FEEDBACK_TASK_KIND_META[kind].hint}</p>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
                <SelectTrigger className="h-10 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden
                          className={cn('h-2 w-2 rounded-full', TICKET_PRIORITY_META[p].dot)}
                        />
                        {TICKET_PRIORITY_META[p].label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {existing ? 'Save' : 'Add task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
